import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Dialog, Portal, CloseButton, Spinner } from '@chakra-ui/react';
import { MagnifyingGlass, X } from '@phosphor-icons/react';
import { useEndpoints } from '../../../hooks/useEndpoints';
import { IBlock } from '../../../models/programming';
import { BLOCK_TYPE_LABELS } from '../../../enums/programming';
import { Paginated } from '../../../typings/Paginated';
import styles from './WorkoutBuilderModal.module.scss';

interface BlockLibraryPickerModalProps {
  isOpen: boolean;
  dayLocalId: string;
  onSelect: (dayLocalId: string, block: IBlock) => void;
  onClose: () => void;
}

export default function BlockLibraryPickerModal({
  isOpen,
  dayLocalId,
  onSelect,
  onClose,
}: BlockLibraryPickerModalProps) {
  const endpoints = useEndpoints();
  const [search, setSearch] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['programming-blocks-picker', search],
    queryFn: async () => {
      const { data } = await endpoints.getProgrammingBlocks({
        limit: 50,
        search: search || undefined,
      });
      return data;
    },
    enabled: isOpen,
  });

  const blocks: IBlock[] = (data as Paginated<IBlock> | undefined)?.docs ?? [];

  return (
    <Dialog.Root
      lazyMount
      unmountOnExit
      open={isOpen}
      size="md"
      placement="center"
      onOpenChange={(e) => { if (!e.open) onClose(); }}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content style={{ maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #e7e7e7' }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--c-text-0)' }}>Scegli blocco dalla libreria</h3>
              <Dialog.CloseTrigger asChild>
                <CloseButton size="sm" />
              </Dialog.CloseTrigger>
            </div>

            {/* Search */}
            <div style={{ padding: '12px 20px', borderBottom: '1px solid #f0f0f0' }}>
              <div style={{ position: 'relative' }}>
                <MagnifyingGlass
                  size={14}
                  style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--c-text-3)' }}
                />
                <input
                  type="text"
                  placeholder="Cerca blocco..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 8px 8px 32px',
                    border: '1px solid #e7e7e7',
                    borderRadius: 8,
                    fontSize: 13,
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch('')}
                    style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-text-3)' }}
                  >
                    <X size={12} weight="bold" />
                  </button>
                )}
              </div>
            </div>

            {/* Lista blocchi */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '12px 20px' }}>
              {isLoading && (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
                  <Spinner size="sm" />
                </div>
              )}
              {!isLoading && blocks.length === 0 && (
                <p style={{ textAlign: 'center', color: 'var(--c-text-3)', fontSize: 13, padding: 40 }}>
                  Nessun blocco trovato
                </p>
              )}
              {blocks.map((block) => (
                <div
                  key={block._id}
                  onClick={() => { onSelect(dayLocalId, block); onClose(); }}
                  style={{
                    padding: '10px 14px',
                    border: '1px solid #e7e7e7',
                    borderRadius: 8,
                    marginBottom: 8,
                    cursor: 'pointer',
                    transition: 'background 0.15s, border-color 0.15s',
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLDivElement).style.background = '#f6f9fa';
                    (e.currentTarget as HTMLDivElement).style.borderColor = '#063444';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLDivElement).style.background = '';
                    (e.currentTarget as HTMLDivElement).style.borderColor = '#e7e7e7';
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--c-text-0)' }}>{block.title}</span>
                    <span style={{ fontSize: 11, color: 'var(--c-text-3)', background: 'var(--c-app-bg)', padding: '2px 8px', borderRadius: 10 }}>
                      {BLOCK_TYPE_LABELS[block.type as keyof typeof BLOCK_TYPE_LABELS] ?? block.type}
                    </span>
                  </div>
                  {block.exercises && block.exercises.length > 0 && (
                    <p style={{ margin: '4px 0 0', fontSize: 11, color: 'var(--c-text-1)' }}>
                      {block.exercises.length} esercizio{block.exercises.length !== 1 ? 'i' : ''}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}
